import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { SellerBalance } from "@/components/SellerBalance";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Dropdown from "react-bootstrap/Dropdown";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  usePickupTimeRanges, PickupTimeRangeSetting, pickupTimeRangeOptions,
} from "@/components/PickupTimeRangeSetting";
import {
  Plus, ChevronLeft, ChevronRight, Truck, Edit, X, Send, Loader2, History, Clock, User,
  ListChecks, CheckCircle2, AlertTriangle, ChevronDown, Check, Search, Upload, Download,
} from "lucide-react";

// Bulk status upload — mirrors MAX_UPLOAD_ROWS / ALLOWED_EXTENSIONS in
// backend/services/pickup_bulk_status_upload_service.py, which enforces them.
const MAX_UPLOAD_ROWS = 100;
const UPLOAD_EXTENSIONS = [".xlsx", ".xls", ".csv"];

// Header-only CSV so a template can never update a real pickup by accident.
const downloadStatusUploadTemplate = () => {
  const blob = new Blob(["Partner_Pick_ID,Status\n"], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "pickup_status_upload_template.csv";
  link.click();
  URL.revokeObjectURL(url);
};

// Most Partner Pickup IDs one search may carry — mirrors MAX_PICKUP_ID_SEARCH
// in backend/routes/pickup_request.py, which enforces it.
const MAX_PICKUP_ID_SEARCH = 25;

// "329489533, 329461039\n329460858" -> unique, non-empty IDs in typed order.
const parsePickupIds = (raw) => [...new Set(raw.split(/[\s,;]+/).filter(Boolean))];

// "Picked *" — Edgistify's marker for a historical Delhivery pickup whose real
// outcome can't be verified (Delhivery deleted the record). NOT a confirmed
// pickup. Mirrors UNVERIFIED_PICKED_* in
// backend/services/pickup_request_status_service.py, which enforces it: set only
// by Bulk Update Status, only from these statuses, only up to the cutoff date,
// and never changed afterwards.
const UNVERIFIED_PICKED = "Picked *";
const UNVERIFIED_PICKED_FROM = ["Scheduled", "Out For Pickup", "Not picked"];
const UNVERIFIED_PICKED_CUTOFF = "2026-08-23";
const UNVERIFIED_PICKED_TOOLTIP =
  "Picked * — unverified. Delhivery's historical pickup record is no longer available, so this pickup could not be confirmed as picked.";

const STATUS_COLORS = {
  "Created": "bg-slate-100 text-slate-800",
  "Scheduled": "bg-blue-100 text-blue-800",
  "Out For Pickup": "bg-yellow-100 text-yellow-800",
  "Picked": "bg-green-100 text-green-800",
  "Not picked": "bg-red-100 text-red-800",
  "Cancelled": "bg-gray-100 text-gray-800",
  // Grey + dashed border: deliberately unlike the green confirmed "Picked".
  [UNVERIFIED_PICKED]: "border border-dashed border-slate-400 bg-slate-50 text-slate-600 hover:bg-slate-50",
};


const STATUSES = ["Created", "Scheduled", "Out For Pickup", "Picked", "Not picked", "Cancelled", UNVERIFIED_PICKED];

// Status badge used in the table, the Update Status dialog and Status History,
// so "Picked *" always carries its explanation.
const StatusBadge = ({ status }) => (
  <Badge
    className={STATUS_COLORS[status] || "bg-gray-100 text-gray-800"}
    title={status === UNVERIFIED_PICKED ? UNVERIFIED_PICKED_TOOLTIP : undefined}
    aria-label={status === UNVERIFIED_PICKED ? UNVERIFIED_PICKED_TOOLTIP : undefined}
  >
    {status}
  </Badge>
);

// Dot colour per status in the Update Status dropdown — same as the pickup
// status email circles (green / yellow / red) and the badges above.
const STATUS_DOT_COLORS = {
  "Created": "#64748b",
  "Scheduled": "#3b82f6",
  "Out For Pickup": "#facc15",
  "Picked": "#10b981",
  "Not picked": "#ef4444",
  "Cancelled": "#6b7280",
  [UNVERIFIED_PICKED]: "#94a3b8",
};

// Status transition map: current status -> allowed next statuses. The approved
// pickup lifecycle — mirrors STATUS_TRANSITIONS in
// backend/services/pickup_request_status_service.py, which enforces it for the
// single + bulk endpoints and the pickup-status email:
//   Created → Scheduled → Out For Pickup → Not picked → Picked
//   Created → Cancelled · Created → Scheduled → Picked
//   Created → Scheduled → Not picked → Picked · Out For Pickup → Picked
//   Not picked → Out For Pickup (re-attempted on its next pickup date)
// (A booked request is cancelled with the Cancel (X) action, which also
// cancels at Delhivery.)
const STATUS_TRANSITIONS = {
  "Created": ["Scheduled", "Cancelled"],
  "Scheduled": ["Out For Pickup", "Picked", "Not picked"],
  "Out For Pickup": ["Picked", "Not picked"],
  "Not picked": ["Out For Pickup", "Picked"],
  // Locked (final) — the backend refuses changes too (LOCKED_STATUSES):
  // Picked = done; Cancelled = the booking was cancelled at Delhivery as well.
  "Picked": [],
  "Cancelled": [],
  // Never changed once set (set only via Bulk Update Status — see BULK_UPDATE_STATUSES).
  [UNVERIFIED_PICKED]: [],
};

// Check if status update should be allowed for a given status
const canUpdateStatus = (status) => {
  return STATUS_TRANSITIONS[status] && STATUS_TRANSITIONS[status].length > 0;
};

// What the Excel/CSV upload may set — mirrors BULK_TARGET_STATUSES in the backend.
const BULK_TARGET_STATUSES = ["Out For Pickup", "Picked", "Not picked", "Scheduled"];
// What "Bulk Update Status" may set — mirrors BULK_UPDATE_STATUSES: the above
// plus "Picked *", which only Bulk Update Status can set.
const BULK_UPDATE_STATUSES = [...BULK_TARGET_STATUSES, UNVERIFIED_PICKED];

// Why Bulk Update Status would skip `row` for `target`, or null if it applies —
// the same rules the backend enforces (_skip_reason).
const bulkSkipReason = (row, target) => {
  if (row.status === target) return `Already ${row.status}`;
  if (row.status === UNVERIFIED_PICKED) return `${UNVERIFIED_PICKED} (unverified) can't be changed`;
  if (target === UNVERIFIED_PICKED) {
    if (!UNVERIFIED_PICKED_FROM.includes(row.status)) {
      return `${row.status} can't be marked ${UNVERIFIED_PICKED}`;
    }
    const day = String(row.pickup_date || "").slice(0, 10);
    if (!day || day > UNVERIFIED_PICKED_CUTOFF) {
      return `Pickup date after ${UNVERIFIED_PICKED_CUTOFF} — not historical`;
    }
    return null;
  }
  if (!(STATUS_TRANSITIONS[row.status] || []).includes(target)) {
    return `${row.status} can't be changed to ${target}`;
  }
  return null;
};

// Group { label, reason } items by reason, so the bulk dialog shows one line
// per reason ("3 · Already Scheduled") however many requests are selected.
const groupByReason = (items) => {
  const groups = new Map();
  items.forEach(({ label, reason }) => {
    if (!groups.has(reason)) groups.set(reason, []);
    groups.get(reason).push(label);
  });
  return [...groups.entries()].map(([reason, labels]) => ({ reason, labels }));
};

// A few example IDs, never the full list: "EDGPR1, EDGPR2, EDGPR3 +12 more".
const previewIds = (labels, max = 3) =>
  labels.length <= max ? labels.join(", ") : `${labels.slice(0, max).join(", ")} +${labels.length - max} more`;

// Colour dot + label for one status, used in the dropdown trigger and its rows.
const StatusOptionLabel = ({ status }) => (
  <span className="flex min-w-0 items-center gap-2">
    <span
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: STATUS_DOT_COLORS[status] || "#64748b" }}
    />
    <span className="truncate">{status}</span>
  </span>
);

// Status dropdown for the single and bulk Update Status dialogs (React-Bootstrap
// Dropdown). Picking only changes the selection — the dialog's "Update Status"
// button saves. Bootstrap's global CSS is deliberately NOT imported (it would
// restyle the whole Tailwind app), so all styling is local Tailwind: the menu is
// hidden until React-Bootstrap adds `.show`. The menu renders inside the dialog
// (no portal), so the Radix modal's focus trap and outside-click handling are
// unaffected; while open, the root is marked data-status-dropdown="open" so the
// dialogs let Esc close the menu instead of the whole dialog.
const StatusSelect = ({ statuses, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1" data-testid={testId}>
      <Dropdown
        show={open}
        onToggle={(next) => setOpen(next)}
        onSelect={(next) => next && onChange(next)}
        className="relative w-full"
        data-status-dropdown={open ? "open" : "closed"}
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          aria-label="New status"
          className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-input bg-white px-3 text-left text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-1 [&[aria-expanded=true]>svg]:rotate-180"
        >
          {value ? <StatusOptionLabel status={value} /> : <span className="text-slate-500">Select status</span>}
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition-transform" />
        </Dropdown.Toggle>
        <Dropdown.Menu
          align="start"
          popperConfig={{ modifiers: [{ name: "offset", options: { offset: [0, 4] } }] }}
          className="z-50 hidden w-full rounded-md border border-slate-200 bg-white p-1 shadow-lg [&.show]:block"
        >
          {statuses.map((status) => (
            <Dropdown.Item
              key={status}
              as="button"
              type="button"
              eventKey={status}
              active={status === value}
              className="flex w-full items-center justify-between gap-2 rounded px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none [&.active]:bg-teal-50 [&.active]:font-semibold"
            >
              <StatusOptionLabel status={status} />
              {status === value && <Check className="h-4 w-4 shrink-0 text-teal-600" />}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

// Dialog onEscapeKeyDown: Esc pressed inside an open status menu closes just the
// menu (React-Bootstrap handles that), not the whole dialog.
const keepDialogOpenForStatusMenu = (event) => {
  if (event.target?.closest?.('[data-status-dropdown="open"]')) event.preventDefault();
};

const ITEMS_PER_PAGE = 50;

const AdminPickupRequests = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  // Admin-configured pickup slots, shared with the other Pickup Requests page.
  const { config: trConfig, saving: trSaving, save: saveTimeRanges } = usePickupTimeRanges();
  const noTimeRanges = (trConfig.options || []).length === 0;
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  // Requests matching the Partner Pickup ID search — the backend pins them first.
  const [matchedTotal, setMatchedTotal] = useState(0);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [pickupIdQuery, setPickupIdQuery] = useState("");
  // What's typed in the Partner Pickup ID box; pickupIdQuery is its debounced,
  // validated form that actually filters.
  const [pickupIdInput, setPickupIdInput] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sellers, setSellers] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [schedulingRequestId, setSchedulingRequestId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    seller_id: "",
    pickup_location_id: "",
    pickup_date: "",
    time_range: "",
    expected_package_count: "",
    notes: ""
  });
  
  const [statusUpdate, setStatusUpdate] = useState("");

  // Bulk status update: selection mode + the selected rows (id -> row), kept
  // across pages so a selection can span them.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState(() => new Map());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(BULK_TARGET_STATUSES[0]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Bulk status upload (Excel / CSV): picked file, request state, server summary.
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [pickupLocations, setPickupLocations] = useState([]);
  // Wallet gate for the selected seller — mirrors admin order creation.
  const [selectedSellerWallet, setSelectedSellerWallet] = useState(null);
  const [isSellerLowBalance, setIsSellerLowBalance] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [currentPage, searchQuery, pickupIdQuery, selectedSeller, selectedStatus, startDate, endDate]);

  // Debounce the Partner Pickup ID box. Over the cap it doesn't search (the
  // hint under the box says why); a new filter starts from page 1 so it never
  // lands on an empty page.
  const pickupIdCount = parsePickupIds(pickupIdInput).length;
  const pickupIdOverLimit = pickupIdCount > MAX_PICKUP_ID_SEARCH;
  useEffect(() => {
    const timer = setTimeout(() => {
      const ids = parsePickupIds(pickupIdInput);
      if (ids.length > MAX_PICKUP_ID_SEARCH) return;
      setPickupIdQuery(ids.join(","));
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [pickupIdInput]);

  // A pasted Excel column / multi-line list would lose its line breaks in a
  // single-line input (gluing the IDs together) — turn them into commas first.
  const handlePickupIdPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!/[\r\n\t]/.test(text)) return;
    e.preventDefault();
    const pasted = text.trim().split(/[\r\n\t]+/).map((t) => t.trim()).filter(Boolean).join(", ");
    const { selectionStart, selectionEnd, value } = e.target;
    setPickupIdInput(value.slice(0, selectionStart) + pasted + value.slice(selectionEnd));
  };

  // When rows reload, refresh the snapshots of any selected ones (so the bulk
  // dialog shows their current status) and drop any that became locked.
  useEffect(() => {
    setSelectedRows((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      requests.forEach((r) => {
        if (!next.has(r.id)) return;
        changed = true;
        if (canUpdateStatus(r.status)) next.set(r.id, r);
        else next.delete(r.id);
      });
      return changed ? next : prev;
    });
  }, [requests]);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      console.error("Failed to fetch sellers");
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
      
      if (searchQuery.trim()) params.append('search', searchQuery);
      if (pickupIdQuery.trim()) params.append('pickup_id', pickupIdQuery.trim());
      if (selectedSeller && selectedSeller !== "all") params.append('seller_id', selectedSeller);
      if (selectedStatus && selectedStatus !== "all") params.append('status', selectedStatus);
      if (startDate) params.append('date_from', startDate);
      if (endDate) params.append('date_to', endDate);
      
      const response = await axios.get(`${API}/pickup-requests/?${params.toString()}`);
      setRequests(response.data.requests);
      setTotalPages(response.data.pages);
      setTotalRequests(response.data.total);
      setMatchedTotal(response.data.matched_total || 0);
    } catch (error) {
      toast.error("Failed to fetch pickup requests");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPickupLocations = async (sellerId) => {
    if (!sellerId) {
      setPickupLocations([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/pickup-locations/seller/${sellerId}`);
      setPickupLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch pickup locations");
      setPickupLocations([]);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setPickupIdQuery("");
    setPickupIdInput("");
    setSelectedSeller("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      seller_id: "",
      pickup_location_id: "",
      pickup_date: "",
      time_range: "",
      expected_package_count: "",
      notes: ""
    });
    setPickupLocations([]);
    setSelectedSellerWallet(null);
    setIsSellerLowBalance(false);
    setShowCreateDialog(true);
  };

  const handleSellerChange = (sellerId) => {
    setFormData({ ...formData, seller_id: sellerId, pickup_location_id: "" });
    fetchPickupLocations(sellerId);
    // Wallet gate — same rule as admin order creation: block create when the selected seller's
    // wallet is at/below the minimum (only when enforcement is enabled for that seller).
    const seller = sellers.find((s) => s.seller_id === sellerId);
    if (seller) {
      const balance = seller.wallet_balance || 0;
      const minBalance = seller.min_wallet_balance || 0;
      const enforce = seller.enforce_wallet_minimum_for_orders !== false;
      setSelectedSellerWallet({ balance, minBalance, enforce });
      setIsSellerLowBalance(enforce && balance <= minBalance);
    } else {
      setSelectedSellerWallet(null);
      setIsSellerLowBalance(false);
    }
  };

  // Tell the sidebar badge to recount now rather than at its next 60s poll, so the
  // blinking dot clears the moment the last pending request is scheduled. Fired only
  // by the handlers that can move a request into or out of "Created" — a plain field
  // edit cannot change status, so it does not need to fire. AdminLayout listens.
  const notifyPickupChange = () =>
    window.dispatchEvent(new Event("pickup-requests:changed"));

  const handleCreateRequest = async () => {
    if (!formData.seller_id || !formData.pickup_location_id || !formData.pickup_date ||
        !formData.time_range || !formData.expected_package_count) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (isSellerLowBalance) {
      toast.error("Seller's wallet balance is below the minimum. Pickup request creation is not allowed.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/pickup-requests/`, {
        seller_id: formData.seller_id,
        pickup_location_id: formData.pickup_location_id,
        pickup_date: formData.pickup_date,
        time_range: formData.time_range,
        expected_package_count: parseInt(formData.expected_package_count),
        notes: formData.notes || null
      });
      
      toast.success("Pickup request created successfully");
      setShowCreateDialog(false);
      fetchRequests();
      notifyPickupChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pickup request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditDialog = async (request) => {
    setSelectedRequest(request);
    setFormData({
      seller_id: request.seller_id,
      pickup_location_id: request.pickup_location_id,
      pickup_date: request.pickup_date,
      time_range: request.time_range,
      expected_package_count: request.expected_package_count.toString(),
      notes: request.notes || ""
    });
    
    await fetchPickupLocations(request.seller_id);
    setShowEditDialog(true);
  };

  const handleUpdateRequest = async () => {
    if (!formData.pickup_date || !formData.time_range || !formData.expected_package_count) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/pickup-requests/${selectedRequest.id}`, {
        pickup_location_id: formData.pickup_location_id,
        pickup_date: formData.pickup_date,
        time_range: formData.time_range,
        expected_package_count: parseInt(formData.expected_package_count),
        notes: formData.notes || null
      });
      
      toast.success("Pickup request updated successfully");
      setShowEditDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pickup request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenStatusDialog = (request) => {
    setSelectedRequest(request);
    // Set the first allowed status as default
    const allowedStatuses = STATUS_TRANSITIONS[request.status] || [];
    setStatusUpdate(allowedStatuses.length > 0 ? allowedStatuses[0] : request.status);
    setShowStatusDialog(true);
  };

  const handleOpenHistoryDialog = (request) => {
    setSelectedRequest(request);
    setShowHistoryDialog(true);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  const handleUpdateStatus = async () => {
    setSubmitting(true);
    try {
      await axios.patch(`${API}/pickup-requests/${selectedRequest.id}/status`, {
        status: statusUpdate
      });
      
      toast.success("Status updated successfully");
      setShowStatusDialog(false);
      fetchRequests();
      notifyPickupChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bulk status update ────────────────────────────────────────────────────────

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedRows(new Map());
  };

  const toggleRowSelected = (request, checked) => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      if (checked) next.set(request.id, request);
      else next.delete(request.id);
      return next;
    });
  };

  // Header checkbox: (de)selects every selectable row on the current page.
  const selectableOnPage = requests.filter((r) => canUpdateStatus(r.status));
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((r) => selectedRows.has(r.id));
  const someOnPageSelected = selectableOnPage.some((r) => selectedRows.has(r.id));
  const toggleAllOnPage = (checked) => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      selectableOnPage.forEach((r) => (checked ? next.set(r.id, r) : next.delete(r.id)));
      return next;
    });
  };

  const openBulkDialog = () => {
    setBulkResult(null);
    setBulkStatus(BULK_TARGET_STATUSES[0]);
    setShowBulkDialog(true);
  };

  // Which selected rows the chosen status would change, and why the rest won't
  // (same rules as the single dialog). The backend re-checks every request.
  const bulkPlan = useMemo(() => {
    const willUpdate = [];
    const willSkip = [];
    selectedRows.forEach((row) => {
      const reason = bulkSkipReason(row, bulkStatus);
      if (reason) willSkip.push({ row, reason });
      else willUpdate.push(row);
    });
    return { willUpdate, willSkip };
  }, [selectedRows, bulkStatus]);

  const handleBulkUpdate = async () => {
    const ids = bulkPlan.willUpdate.map((r) => r.id);
    // The dialog itself is the confirmation (count, target status, update/skip
    // preview) — no native confirm(). bulkSubmitting blocks double submits.
    if (ids.length === 0 || bulkSubmitting) return;

    setBulkSubmitting(true);
    try {
      const { data } = await axios.patch(`${API}/pickup-requests/bulk-status`, {
        request_ids: ids,
        status: bulkStatus,
      });

      // Everything not updated: rows skipped here before sending + rows the
      // server skipped (locked, already in status, changed meanwhile, not found).
      const skipped = [
        ...bulkPlan.willSkip.map(({ row, reason }) => ({ request_id: row.request_id, message: reason })),
        ...data.skipped.map((s) => ({ request_id: s.request_id || s.id, message: s.message })),
      ];

      fetchRequests();
      notifyPickupChange();
      exitSelectionMode();

      if (skipped.length === 0) {
        toast.success(`Updated ${data.updated_count} pickup request(s) to ${bulkStatus}`);
        setShowBulkDialog(false);
      } else {
        const summary = `Updated ${data.updated_count}, ${skipped.length} not updated`;
        if (data.updated_count > 0) toast.warning(summary);
        else toast.error(summary);
        setBulkResult({ status: bulkStatus, updated: data.updated, skipped });
      }
    } catch (error) {
      // Selection and dialog stay as they were so the user can retry.
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't update statuses. Refresh to check what changed, then try again."
      );
    } finally {
      setBulkSubmitting(false);
    }
  };

  const closeUploadDialog = () => {
    if (uploading) return; // never close mid-request
    setShowUploadDialog(false);
    setUploadFile(null);
    setUploadError("");
    setUploadResult(null);
  };

  const handleUploadStatus = async () => {
    if (!uploadFile || uploading) return;
    const name = uploadFile.name.toLowerCase();
    if (!UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      setUploadError(`Only ${UPLOAD_EXTENSIONS.join(", ")} files are supported`);
      return;
    }

    setUploading(true);
    setUploadError("");
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      const { data } = await axios.post(`${API}/pickup-requests/bulk-status/upload`, form);
      setUploadResult(data);
      if (data.updated_count > 0) {
        fetchRequests();
        notifyPickupChange();
      }
      const summary = `Updated ${data.updated_count} of ${data.total_rows}, ${data.failed_count} failed`;
      if (data.failed_count === 0) toast.success(summary);
      else if (data.updated_count > 0) toast.warning(summary);
      else toast.error(summary);
    } catch (error) {
      // File-level problems (columns, > limit, unreadable): nothing was updated.
      const detail = error.response?.data?.detail;
      setUploadError(typeof detail === "string" ? detail : "Upload failed. Nothing was updated — try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleCancelRequest = async (request) => {
    const confirmed = window.confirm(`Are you sure you want to cancel pickup request ${request.request_id}?`);
    if (!confirmed) return;

    try {
      await axios.patch(`${API}/pickup-requests/${request.id}/cancel`);
      toast.success("Pickup request cancelled");
      fetchRequests();
      notifyPickupChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  const handleScheduleToPartner = async (request) => {
    const confirmed = window.confirm(
      `Schedule pickup request ${request.request_id} to Delhivery?\n\nPickup Date: ${request.pickup_date}\nTime: ${request.time_range}`
    );
    if (!confirmed) return;

    setSchedulingRequestId(request.id);
    
    try {
      const response = await axios.post(`${API}/pickup-requests/${request.id}/schedule-to-partner`);
      toast.success("Pickup request scheduled to Delhivery successfully!");
      fetchRequests();
      notifyPickupChange();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to schedule pickup request";
      toast.error(errorMsg);
    } finally {
      setSchedulingRequestId(null);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Pickup Requests</h1>
            <p className="text-slate-600 mt-1">Manage pickup requests and scheduling</p>
          </div>
          {/* Order: [Bulk Update Status + Upload Bulk Status | Cancel Selection
              (+ Update Status)] [Time Range] [Create Request]. Wraps on small screens. */}
          <div className="flex flex-wrap items-end gap-4">
            {!selectionMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setSelectionMode(true)}
                  data-testid="bulk-update-status-btn"
                >
                  <ListChecks className="w-4 h-4 mr-2" />
                  Bulk Update Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowUploadDialog(true)}
                  data-testid="upload-bulk-status-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Bulk Status
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={exitSelectionMode} data-testid="cancel-selection-btn">
                  <X className="w-4 h-4 mr-2" />
                  Cancel Selection
                </Button>
                {selectedRows.size > 0 && (
                  <Button onClick={openBulkDialog} data-testid="open-bulk-update-btn">
                    <Truck className="w-4 h-4 mr-2" />
                    Update Status ({selectedRows.size} Selected)
                  </Button>
                )}
              </>
            )}
            <PickupTimeRangeSetting config={trConfig} saving={trSaving} onSave={saveTimeRanges} />
            <Button onClick={handleOpenCreateDialog} disabled={noTimeRanges}
              title={noTimeRanges ? "No pickup time ranges are currently available" : undefined}>
              <Plus className="w-4 h-4 mr-2" />
              Create Request
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Search</Label>
                <SearchInput
                  onSearch={setSearchQuery}
                  placeholder="Search by Request ID..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="pickup-id-search" className="text-sm">Partner Pickup ID</Label>
                <div className="relative mt-1 w-full">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="pickup-id-search"
                    type="text"
                    value={pickupIdInput}
                    onChange={(e) => setPickupIdInput(e.target.value)}
                    onPaste={handlePickupIdPaste}
                    placeholder={`Up to ${MAX_PICKUP_ID_SEARCH} IDs, comma separated...`}
                    className={`pl-10 ${pickupIdOverLimit ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    aria-invalid={pickupIdOverLimit}
                    aria-describedby="pickup-id-search-hint"
                    data-testid="pickup-id-search"
                  />
                </div>
                {pickupIdCount > 1 && (
                  <p
                    id="pickup-id-search-hint"
                    className={`mt-1 text-xs ${pickupIdOverLimit ? "text-red-600" : "text-slate-500"}`}
                  >
                    {pickupIdOverLimit
                      ? `${pickupIdCount} IDs — remove ${pickupIdCount - MAX_PICKUP_ID_SEARCH} to search (max ${MAX_PICKUP_ID_SEARCH})`
                      : `${pickupIdCount} / ${MAX_PICKUP_ID_SEARCH} IDs`}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="text-sm">Seller</Label>
                <Select value={selectedSeller} onValueChange={setSelectedSeller}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Sellers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sellers</SelectItem>
                    {sellers.map((seller) => (
                      <SelectItem key={seller.seller_id} value={seller.seller_id}>
                        {seller.brand_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm">From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-end">
                <Button variant="outline" onClick={handleClearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table — fixed height whatever the row count: roughly the
            viewport below the header + filters, never under 24rem on phones or
            48rem from md up (header ~40px + 10 rows x ~65px + pagination ~57px,
            so at least 10 rows show before scrolling). Only the rows area
            scrolls; the selection bar and pagination stay in view. */}
        <Card className="flex h-[max(24rem,calc(100vh-24rem))] flex-col overflow-hidden md:h-[max(48rem,calc(100vh-24rem))]">
          {/* Bulk selection hint — outside the scroll area, so always visible */}
          {selectionMode && (
            <div
              className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-teal-200 bg-teal-50 px-4 py-2 text-sm text-teal-800"
              data-testid="bulk-selection-bar"
            >
              <span>
                <strong>{selectedRows.size}</strong> selected
              </span>
              <span className="text-teal-700">
                Tick the pickup requests to update. Picked and Cancelled requests can't be changed.
              </span>
              {/* No separate "Clear selection" — Cancel Selection in the header does it. */}
            </div>
          )}
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            {loading ? (
              <div className="flex flex-1 items-center justify-center p-8 text-center text-slate-600">Loading...</div>
            ) : requests.length === 0 ? (
              <div className="flex flex-1 items-center justify-center p-8 text-center text-slate-600">No pickup requests found</div>
            ) : (
              <>
                {/* The one scroll area (both axes). scrollbar-gutter reserves the
                    scrollbar's space so columns don't shift when it appears. */}
                <div
                  className="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]"
                  data-testid="pickup-requests-scroll"
                >
                  <table className="w-full">
                    {/* Sticky header. Collapsed table borders don't stick, so the
                        bottom rule is a box-shadow instead of border-b. */}
                    <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
                      <tr>
                        {selectionMode && (
                          <th className="w-10 px-4 py-3 text-left">
                            <Checkbox
                              checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                              onCheckedChange={(value) => toggleAllOnPage(value === true)}
                              disabled={selectableOnPage.length === 0}
                              aria-label="Select all updatable requests on this page"
                              data-testid="select-all-on-page"
                            />
                          </th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Request ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Seller</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pickup Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pickup Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Time Range</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Package Count</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Partner Pickup ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {requests.map((request) => (
                        <tr
                          key={request.id}
                          className={
                            selectedRows.has(request.id)
                              ? "bg-teal-50/60 hover:bg-teal-50"
                              : request.pickup_id_match
                                ? "bg-amber-50 hover:bg-amber-100/70"
                                : "hover:bg-slate-50"
                          }
                          data-pickup-id-match={request.pickup_id_match ? "true" : undefined}
                        >
                          {selectionMode && (
                            <td className="w-10 px-4 py-3 align-middle">
                              <Checkbox
                                checked={selectedRows.has(request.id)}
                                onCheckedChange={(value) => toggleRowSelected(request, value === true)}
                                disabled={!canUpdateStatus(request.status)}
                                title={
                                  canUpdateStatus(request.status)
                                    ? undefined
                                    : `${request.status} — status can't be changed`
                                }
                                aria-label={`Select ${request.request_id}`}
                                data-testid={`select-request-${request.id}`}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 align-middle text-sm">
                            <span className="font-medium text-teal-600">{request.request_id}</span>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="font-medium text-slate-800">{request.seller_brand_name || request.seller_id}</div>
                            <SellerBalance wallet={request.seller_wallet} />
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div>{request.pickup_location_name}</div>
                            <div className="text-xs text-slate-500">{request.pickup_location_city}</div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">{request.pickup_date}</td>
                          <td className="px-4 py-3 align-middle text-sm">{request.time_range}</td>
                          <td className="px-4 py-3 align-middle text-sm text-right">{request.expected_package_count}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            {request.shipping_partner_pickup_id ? (
                              <span className="font-mono text-xs text-teal-700 bg-teal-50 px-2 py-1 rounded">
                                {request.shipping_partner_pickup_id}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <StatusBadge status={request.status} />
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex gap-2">
                              {request.status === "Created" && !request.scheduled_to_partner && (
                                <Button
                                  size="sm"
                                  style={{ backgroundColor: '#00A699' }}
                                  onClick={() => handleScheduleToPartner(request)}
                                  disabled={schedulingRequestId === request.id}
                                  title="Schedule to Delhivery"
                                >
                                  {schedulingRequestId === request.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {canUpdateStatus(request.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenStatusDialog(request)}
                                  title="Update Status"
                                  data-testid={`update-status-btn-${request.id}`}
                                >
                                  <Truck className="w-4 h-4" />
                                </Button>
                              )}
                              {request.status === "Created" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenEditDialog(request)}
                                  title="Edit Request"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {(request.status === "Created" || request.status === "Scheduled") && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCancelRequest(request)}
                                  title="Cancel Request"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenHistoryDialog(request)}
                                title="View Status History"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination — pinned under the scroll area, never overlapping it */}
                <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    Showing {requests.length} of {totalRequests} requests
                    {pickupIdQuery && (
                      <span className={matchedTotal ? "text-amber-700" : "text-slate-500"} data-testid="pickup-id-match-count">
                        {" · "}
                        {matchedTotal
                          ? `${matchedTotal} matching Pickup ID${matchedTotal === 1 ? "" : "s"} shown first`
                          : "No request matches the searched Pickup ID(s)"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Pickup Request</DialogTitle>
            <DialogDescription>Schedule a new pickup request for a seller</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {isSellerLowBalance && selectedSellerWallet && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 font-semibold text-sm">Low Balance Alert</p>
                <p className="text-sm text-red-700 mt-1">
                  This seller&apos;s wallet balance (₹{selectedSellerWallet.balance?.toLocaleString()}) is at or below the
                  minimum threshold (₹{selectedSellerWallet.minBalance?.toLocaleString()}). Pickup request creation is not
                  allowed until the wallet is recharged.
                </p>
              </div>
            )}
            <div>
              <Label>Seller *</Label>
              <Select value={formData.seller_id} onValueChange={handleSellerChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select seller" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((seller) => (
                    <SelectItem key={seller.seller_id} value={seller.seller_id}>
                      {seller.brand_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Pickup Location *</Label>
              <Select 
                value={formData.pickup_location_id} 
                onValueChange={(val) => setFormData({...formData, pickup_location_id: val})}
                disabled={!formData.seller_id}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pickup location" />
                </SelectTrigger>
                <SelectContent>
                  {pickupLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.facility_name} - {loc.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Time Range *</Label>
              <Select 
                value={formData.time_range} 
                onValueChange={(val) => setFormData({...formData, time_range: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  {pickupTimeRangeOptions(trConfig, formData.time_range).map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Expected Package Count *</Label>
              <Input
                type="number"
                min="1"
                value={formData.expected_package_count}
                onChange={(e) => setFormData({...formData, expected_package_count: e.target.value})}
                className="mt-1"
                placeholder="Enter package count"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1"
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRequest} disabled={submitting || isSellerLowBalance}>
              {submitting ? "Creating..." : "Create Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Pickup Request</DialogTitle>
            <DialogDescription>Update pickup request details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Pickup Location *</Label>
              <Select 
                value={formData.pickup_location_id} 
                onValueChange={(val) => setFormData({...formData, pickup_location_id: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pickup location" />
                </SelectTrigger>
                <SelectContent>
                  {pickupLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.facility_name} - {loc.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Time Range *</Label>
              <Select 
                value={formData.time_range} 
                onValueChange={(val) => setFormData({...formData, time_range: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  {pickupTimeRangeOptions(trConfig, formData.time_range).map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Expected Package Count *</Label>
              <Input
                type="number"
                min="1"
                value={formData.expected_package_count}
                onChange={(e) => setFormData({...formData, expected_package_count: e.target.value})}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRequest} disabled={submitting}>
              {submitting ? "Updating..." : "Update Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md" onEscapeKeyDown={keepDialogOpenForStatusMenu}>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              Change the status of pickup request: {selectedRequest?.request_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-3 p-3 bg-slate-50 rounded-md">
              <span className="text-sm text-slate-600">Current Status: </span>
              <StatusBadge status={selectedRequest?.status} />
            </div>
            <Label>New Status</Label>
            {/* Saved by "Update Status" below (PATCH /pickup-requests/{id}/status). */}
            <StatusSelect
              statuses={STATUS_TRANSITIONS[selectedRequest?.status] || []}
              value={statusUpdate}
              onChange={setStatusUpdate}
              testId="status-update-select"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={submitting} data-testid="update-status-submit-btn">
              {submitting ? "Updating..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog
        open={showBulkDialog}
        onOpenChange={(open) => {
          if (bulkSubmitting) return; // never close mid-request
          setShowBulkDialog(open);
          if (!open) setBulkResult(null);
        }}
      >
        <DialogContent className="max-w-lg" onEscapeKeyDown={keepDialogOpenForStatusMenu}>
          {!bulkResult ? (
            <>
              <DialogHeader>
                <DialogTitle>Update Status</DialogTitle>
                <DialogDescription>
                  Change the status of {selectedRows.size} selected pickup request
                  {selectedRows.size === 1 ? "" : "s"}.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* A count, not a list — stays compact with 50+ selected. */}
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="bulk-selected-count">
                  <div className="text-sm font-medium text-slate-700">Selected Requests</div>
                  <div className="text-sm text-slate-900">
                    <strong>{selectedRows.size}</strong> pickup request{selectedRows.size === 1 ? "" : "s"} selected
                  </div>
                </div>

                <div>
                  <Label>New Status</Label>
                  <StatusSelect
                    statuses={BULK_UPDATE_STATUSES}
                    value={bulkStatus}
                    onChange={setBulkStatus}
                    testId="bulk-status-select"
                  />
                </div>

                {bulkStatus === UNVERIFIED_PICKED && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-dashed border-slate-400 bg-slate-50 p-3 text-sm text-slate-700"
                    data-testid="unverified-picked-warning"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <div>
                      <div className="font-medium text-slate-800">Unverified marker — not a confirmed pickup</div>
                      <p className="mt-1">
                        Use {UNVERIFIED_PICKED} only for historical pickups whose Delhivery record is no longer
                        available. Only {UNVERIFIED_PICKED_FROM.join(", ")} requests dated on or before{" "}
                        {UNVERIFIED_PICKED_CUTOFF} are updated.
                      </p>
                      <p className="mt-1 font-medium">Once set, {UNVERIFIED_PICKED} can't be changed.</p>
                    </div>
                  </div>
                )}

                <div
                  className={`rounded-md border p-3 text-sm ${
                    bulkPlan.willSkip.length ? "border-amber-200 bg-amber-50" : "border-teal-200 bg-teal-50"
                  }`}
                  data-testid="bulk-update-preview"
                >
                  <div className="font-medium text-slate-800">
                    Will update {bulkPlan.willUpdate.length} · Will skip {bulkPlan.willSkip.length}
                  </div>
                  {bulkPlan.willSkip.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-amber-800">
                      {groupByReason(
                        bulkPlan.willSkip.map(({ row, reason }) => ({ label: row.request_id, reason }))
                      ).map(({ reason, labels }) => (
                        <li key={reason}>
                          <span className="font-medium">{labels.length} · {reason}</span>
                          <span className="text-amber-700"> ({previewIds(labels)})</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBulkDialog(false)} disabled={bulkSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkUpdate}
                  disabled={bulkSubmitting || bulkPlan.willUpdate.length === 0}
                  data-testid="bulk-update-submit-btn"
                >
                  {bulkSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Status"
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Bulk update finished</DialogTitle>
                <DialogDescription>
                  {bulkResult.updated.length} updated to {bulkResult.status} ·{" "}
                  {bulkResult.skipped.length} not updated
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-2 text-sm" data-testid="bulk-update-result">
                {bulkResult.updated.length > 0 && (
                  <div className="rounded-md border border-teal-200 bg-teal-50 p-3">
                    <div className="mb-1 flex items-center gap-1.5 font-medium text-teal-800">
                      <CheckCircle2 className="w-4 h-4" />
                      Updated ({bulkResult.updated.length})
                    </div>
                    <div className="text-xs text-teal-900">
                      {previewIds(bulkResult.updated.map((u) => u.request_id || u.id))}
                    </div>
                  </div>
                )}
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="mb-1 flex items-center gap-1.5 font-medium text-amber-800">
                    <AlertTriangle className="w-4 h-4" />
                    Not updated ({bulkResult.skipped.length})
                  </div>
                  <ul className="space-y-1 text-xs text-amber-900">
                    {groupByReason(
                      bulkResult.skipped.map((s) => ({ label: s.request_id, reason: s.message }))
                    ).map(({ reason, labels }) => (
                      <li key={reason}>
                        <span className="font-medium">{labels.length} · {reason}</span>
                        <span className="text-amber-700"> ({previewIds(labels)})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowBulkDialog(false);
                    setBulkResult(null);
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Status Upload Dialog (Excel / CSV) */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => !open && closeUploadDialog()}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
          {!uploadResult ? (
            <>
              <DialogHeader>
                <DialogTitle>Upload Bulk Status</DialogTitle>
                <DialogDescription>
                  Update pickup statuses from an Excel or CSV file, matched by Partner Pickup ID.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 overflow-y-auto py-2 text-sm">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                  <div className="font-medium text-slate-800">File format</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    <li>
                      Columns: <code className="rounded bg-white px-1">Partner_Pick_ID</code> and{" "}
                      <code className="rounded bg-white px-1">Status</code>
                    </li>
                    <li>Status: {BULK_TARGET_STATUSES.join(", ")}</li>
                    <li>
                      Up to <strong>{MAX_UPLOAD_ROWS}</strong> rows per upload · {UPLOAD_EXTENSIONS.join(", ")}
                    </li>
                    <li>Cancel pickups with the Cancel action, not this upload.</li>
                  </ul>
                  <Button
                    variant="link"
                    className="mt-1 h-auto p-0 text-teal-700"
                    onClick={downloadStatusUploadTemplate}
                    data-testid="download-status-template-btn"
                  >
                    <Download className="mr-1 h-4 w-4" />
                    Download template
                  </Button>
                </div>

                <div>
                  <Label htmlFor="bulk-status-file">File</Label>
                  <Input
                    id="bulk-status-file"
                    type="file"
                    accept={UPLOAD_EXTENSIONS.join(",")}
                    onChange={(e) => {
                      setUploadFile(e.target.files?.[0] || null);
                      setUploadError("");
                    }}
                    disabled={uploading}
                    className="mt-1 cursor-pointer"
                    data-testid="bulk-status-file-input"
                  />
                </div>

                {uploadError && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-800"
                    role="alert"
                    data-testid="bulk-status-upload-error"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeUploadDialog} disabled={uploading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadStatus}
                  disabled={!uploadFile || uploading}
                  data-testid="bulk-status-upload-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Update
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Upload finished</DialogTitle>
                <DialogDescription>{uploadFile?.name}</DialogDescription>
              </DialogHeader>

              <div className="min-h-0 space-y-3 overflow-y-auto py-2 text-sm" data-testid="bulk-status-upload-result">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                    <div className="text-xl font-semibold text-slate-900">{uploadResult.total_rows}</div>
                    <div className="text-xs text-slate-600">Rows processed</div>
                  </div>
                  <div className="rounded-md border border-teal-200 bg-teal-50 p-2">
                    <div className="text-xl font-semibold text-teal-800">{uploadResult.updated_count}</div>
                    <div className="text-xs text-teal-700">Updated</div>
                  </div>
                  <div
                    className={`rounded-md border p-2 ${
                      uploadResult.failed_count ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div
                      className={`text-xl font-semibold ${uploadResult.failed_count ? "text-amber-800" : "text-slate-900"}`}
                    >
                      {uploadResult.failed_count}
                    </div>
                    <div className={`text-xs ${uploadResult.failed_count ? "text-amber-700" : "text-slate-600"}`}>
                      Failed / unmatched
                    </div>
                  </div>
                </div>

                {uploadResult.failed.length > 0 && (
                  <div className="rounded-md border border-amber-200">
                    <div className="flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-3 py-2 font-medium text-amber-800">
                      <AlertTriangle className="w-4 h-4" />
                      Not updated ({uploadResult.failed.length})
                    </div>
                    <div className="max-h-60 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white text-left text-slate-600 shadow-[0_1px_0_0_#e2e8f0]">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Row</th>
                            <th className="px-3 py-2 font-semibold whitespace-nowrap">Partner Pickup ID</th>
                            <th className="px-3 py-2 font-semibold">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {uploadResult.failed.map((f) => (
                            <tr key={`${f.row_number}-${f.partner_pickup_id}`}>
                              <td className="px-3 py-1.5 align-top text-slate-500">{f.row_number}</td>
                              <td className="px-3 py-1.5 align-top font-mono text-slate-800">
                                {f.partner_pickup_id || "-"}
                              </td>
                              <td className="px-3 py-1.5 align-top text-amber-900">{f.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {uploadResult.updated.length > 0 && (
                  <div className="rounded-md border border-teal-200">
                    <div className="flex items-center gap-1.5 border-b border-teal-200 bg-teal-50 px-3 py-2 font-medium text-teal-800">
                      <CheckCircle2 className="w-4 h-4" />
                      Updated ({uploadResult.updated.length})
                    </div>
                    <div className="max-h-48 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white text-left text-slate-600 shadow-[0_1px_0_0_#e2e8f0]">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Row</th>
                            <th className="px-3 py-2 font-semibold whitespace-nowrap">Partner Pickup ID</th>
                            <th className="px-3 py-2 font-semibold">Request</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {uploadResult.updated.map((u) => (
                            <tr key={`${u.row_number}-${u.partner_pickup_id}`}>
                              <td className="px-3 py-1.5 text-slate-500">{u.row_number}</td>
                              <td className="px-3 py-1.5 font-mono text-slate-800">{u.partner_pickup_id}</td>
                              <td className="px-3 py-1.5 text-teal-700">{u.request_id}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                                {u.previous_status} → {u.status}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button onClick={closeUploadDialog} data-testid="bulk-status-upload-done-btn">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Status History
            </DialogTitle>
            <DialogDescription>
              Pickup Request: {selectedRequest?.request_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {selectedRequest?.status_history && selectedRequest.status_history.length > 0 ? (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-200"></div>
                
                <div className="space-y-4">
                  {[...selectedRequest.status_history].reverse().map((entry, index) => (
                    <div key={index} className="relative flex gap-4 pl-10">
                      {/* Timeline dot */}
                      <div className={`absolute left-2.5 w-3 h-3 rounded-full ${
                        index === 0 ? 'bg-[#00A699]' : 'bg-slate-300'
                      }`}></div>
                      
                      <div className="flex-1 bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <StatusBadge status={entry.status} />
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(entry.timestamp)}
                          </span>
                        </div>
                        
                        {entry.updated_by_name && (
                          <div className="flex items-center gap-1 text-sm text-slate-600 mb-1">
                            <User className="w-3 h-3" />
                            <span>{entry.updated_by_name}</span>
                            {entry.updated_by_role && (
                              <span className="text-xs text-slate-400">({entry.updated_by_role})</span>
                            )}
                          </div>
                        )}
                        
                        {entry.remarks && (
                          <p className="text-sm text-slate-600 mt-1 border-t border-slate-200 pt-1">
                            {entry.remarks}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No status history available</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPickupRequests;
